import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { AiAgentDetailsPage } from 'src/components/pages/ai-agents/agents/ai-agent-details-page';
import { AiAgentsListPage } from 'src/components/pages/ai-agents/agents/ai-agents-list-page';
import { AnalyticsPage } from 'src/components/pages/ai-agents/analytics/analytics-page';
import { KnowledgeBaseDetailsPage } from 'src/components/pages/ai-agents/knowledge-bases/knowledge-base-details-page';
import { KnowledgeBasesListPage } from 'src/components/pages/ai-agents/knowledge-bases/knowledge-bases-list-page';
import { useAddBreadcrumb } from 'src/hooks/use-add-breadcrumb';

const SectionBreadcrumb = memo<{ label: string }>(({ label }) => {
  useAddBreadcrumb(label);

  return null;
});

type AiAgentsPageProps = {
  canViewAgents: boolean;
  canViewKnowledgeBases: boolean;
};

export const AiAgentsPage = memo<AiAgentsPageProps>(
  ({ canViewAgents, canViewKnowledgeBases }) => {
    const { t } = useTranslation(['aiAgents']);
    const { path, url } = useRouteMatch();
    const knowledgeBasesMatch = useRouteMatch(`${path}/knowledge-bases`);

    return (
      <>
        {!knowledgeBasesMatch && (
          <SectionBreadcrumb label={t('aiAgents:pages.agents.breadcrumb')} />
        )}
        <Switch>
          {canViewAgents && (
            <Route exact path={`${path}/agents`}>
              <AiAgentsListPage sectionUrl={url} />
            </Route>
          )}
          {canViewAgents && (
            <Route path={`${path}/agents/:agentId`}>
              <AiAgentDetailsPage sectionUrl={url} />
            </Route>
          )}
          {canViewAgents && (
            <Route exact path={`${path}/analytics`}>
              <AnalyticsPage />
            </Route>
          )}
          {canViewKnowledgeBases && (
            <Route path={`${path}/knowledge-bases`}>
              <SectionBreadcrumb label={t('aiAgents:pages.knowledgeBases.breadcrumb')} />
              <Switch>
                <Route exact path={`${path}/knowledge-bases`}>
                  <KnowledgeBasesListPage sectionUrl={url} />
                </Route>
                <Route path={`${path}/knowledge-bases/:knowledgeBaseId`}>
                  <KnowledgeBaseDetailsPage sectionUrl={url} />
                </Route>
              </Switch>
            </Route>
          )}
          <Redirect to={canViewAgents ? `${url}/agents` : `${url}/knowledge-bases`} />
        </Switch>
      </>
    );
  },
);
